import type { MappingMonitoringStatus, MonitoringCadence } from "@price-rescue/api-contracts";

export type { MonitoringCadence };
export type MonitoringStatus = MappingMonitoringStatus;
export type MonitoringHealthFilter =
  "" | "DUE" | "HEALTHY" | "FAILING" | "CHANGED" | "STALE" | "PAUSED";

export interface MonitoringFilters {
  competitorDomain?: string | undefined;
  currency?: string | undefined;
  cursor?: string | undefined;
  health?: MonitoringHealthFilter | undefined;
  limit?: number | undefined;
  mappingId?: string | undefined;
  productId?: string | undefined;
  sort?: "nextCheck" | "lastCheck" | "health" | "product" | undefined;
}

export interface MonitoringMapping {
  mappingId: string;
  storeHash: string;
  platform: string;
  platformProductId: string;
  competitorDomain: string;
  competitorUrl: string;
  status: MonitoringStatus;
  monitoringEnabled: boolean;
  cadence?: MonitoringCadence;
  timezone?: string;
  nextCheckAt?: string | null;
  lastCheckedAt?: string | null;
  latestCompetitorPriceMinor?: number | null;
  latestCurrency?: string | null;
  latestAvailability?: string | null;
}

export interface MonitoringCounts {
  totalVerified: number;
  enabled: number;
  due: number;
  healthy: number;
  failing: number;
  changed: number;
  stale: number;
}

export interface MonitoringPlanLimits {
  maxMonitoredMappings: number;
  allowedCadences: MonitoringCadence[];
}

export interface MonitoringSummary {
  mappings: MonitoringMapping[];
  nextCursor: string | null;
  counts: MonitoringCounts;
  planLimits: MonitoringPlanLimits;
}

export interface MonitoringObservation {
  priceMinor: number | null;
  currency: string | null;
  availability: string | null;
  sellerName?: string | null;
  identityConfidence?: number | null;
  extractionConfidence?: number | null;
  extractionSucceeded?: boolean | null;
  observedAt: string;
}

export interface MonitoringObservationPage {
  observations: MonitoringObservation[];
  nextCursor: string | null;
}

export interface MonitoringSettingsDraft {
  mappingId?: string | undefined;
  productId?: string | undefined;
  enabled: boolean;
  cadence: MonitoringCadence;
  timezone: string;
  priceChangeThresholdMinor: number;
  stockChangeAlerts: boolean;
  recipients: string;
}

export interface MonitoringRunNowResult {
  jobId: string;
  jobType: string;
  status: string;
  payload?: Record<string, unknown>;
  createdAt?: string;
}

export interface NotificationPreferenceRule {
  eventType: string;
  channel: "EMAIL" | "IN_APP";
  enabled: boolean;
  scope?: string;
}

export interface NotificationPreferenceRecord {
  rules: NotificationPreferenceRule[];
  updatedAt: string;
  updatedBy: string;
}

export function defaultMonitoringFilters(): MonitoringFilters {
  return { limit: 50, health: "", sort: "nextCheck" };
}

export function defaultMonitoringSettingsDraft(): MonitoringSettingsDraft {
  return {
    enabled: true,
    cadence: "DAILY",
    timezone: "UTC",
    priceChangeThresholdMinor: 100,
    stockChangeAlerts: true,
    recipients: "",
  };
}

export function settingsDraftFromMapping(mapping: MonitoringMapping): MonitoringSettingsDraft {
  return {
    mappingId: mapping.mappingId,
    productId: mapping.platformProductId,
    enabled: mapping.monitoringEnabled,
    cadence: mapping.cadence ?? "DAILY",
    timezone: mapping.timezone ?? "UTC",
    priceChangeThresholdMinor: 100,
    stockChangeAlerts: true,
    recipients: "",
  };
}

export function classifyMonitoringMapping(
  mapping: MonitoringMapping,
  now = new Date(),
): MonitoringHealthFilter {
  if (!mapping.monitoringEnabled) return "PAUSED";
  if (
    mapping.lastCheckedAt !== undefined &&
    mapping.lastCheckedAt !== null &&
    mapping.latestCompetitorPriceMinor === null
  )
    return "FAILING";
  if (mapping.latestAvailability === "OUT_OF_STOCK" || mapping.latestAvailability === "LIMITED")
    return "CHANGED";
  if (
    mapping.lastCheckedAt !== undefined &&
    mapping.lastCheckedAt !== null &&
    isStale(mapping, now)
  )
    return "STALE";
  if (
    mapping.nextCheckAt !== undefined &&
    mapping.nextCheckAt !== null &&
    new Date(mapping.nextCheckAt).getTime() <= now.getTime()
  )
    return "DUE";
  return "HEALTHY";
}

export function buildMonitoringPreferenceRules(
  draft: MonitoringSettingsDraft,
): NotificationPreferenceRule[] {
  return [
    {
      eventType: "PRICE_CHANGED",
      channel: "EMAIL",
      enabled: draft.priceChangeThresholdMinor >= 0,
      scope: "USER",
    },
    {
      eventType: "STOCK_CHANGED",
      channel: "EMAIL",
      enabled: draft.stockChangeAlerts,
      scope: "USER",
    },
    { eventType: "MONITORING_FAILED", channel: "IN_APP", enabled: true, scope: "USER" },
  ];
}

function isStale(mapping: MonitoringMapping, now: Date): boolean {
  const checkedAt = new Date(mapping.lastCheckedAt ?? now.toISOString()).getTime();
  const cadenceHours =
    mapping.cadence === "WEEKLY" ? 14 * 24 : mapping.cadence === "TWICE_DAILY" ? 24 : 48;
  return now.getTime() - checkedAt > cadenceHours * 60 * 60 * 1000;
}
